// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import React, {useId, useMemo} from 'react';
import cx from 'classnames';
import {Text} from '@albert-io/atomic';
import type {QuestionModelV3} from '@albert-io/models';
import {List} from 'immutable';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import {
  getSmallSupplement,
  SMALL_SUPPLEMENT_WRAPPER_CLASS,
  SupplementChip
} from 'client/components/QuestionTypes/shared/DraggableChip';
import {OrderTable, OrderTableCell} from './OrderTable.react';

interface Props {
  question: QuestionModelV3;
}

export const CorrectAnswerSection = ({question}: Props) => {
  const useStoreHook = useQuestionStore<'ordering'>(question);

  // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const storeQuestion = useStoreHook((state) => state.question);

  const correctAnswerList = useMemo(() => {
    const items =
      storeQuestion?.getQuestionTypeAttributes()?.getIn(['ordering', 'items'], List()).toJS() || [];
    const slots =
      storeQuestion?.getQuestionTypeAttributes()?.getIn(['ordering', 'slots'], List()).toJS() || [];

    let validResponse: Record<string, string> = {};
    try {
      validResponse = storeQuestion?.getValidResponse()?.toJS() || {};
    } catch (_e) {
      // no valid response
    }

    const itemById: Record<string, any> = {};
    items.forEach((item: {id: string; value?: string | string[]}) => {
      itemById[item.id] = item;
    });

    // Correct order is defined by slots: for each slot position, find the mapped item
    return slots.map((slot: {id: string}, index: number) => {
      const itemId = validResponse[slot.id];
      const item = itemId ? itemById[itemId] : null;
      const id = item?.id || `word-${index}`;
      const value = item ? (Array.isArray(item.value) ? `[${item.value}]` : item.value || '') : '';
      return {id, value};
    });
  }, [storeQuestion]);

  const headingId = useId();

  return (
    <>
      <Text id={headingId} className='mt-x4 mb-x2'>
        Correct Order:
      </Text>
      {correctAnswerList.length > 0 && (
        <OrderTable labelledBy={headingId} className='mb-x2'>
          {correctAnswerList.map((item, index) => {
            // Match the student answer's Item layout: constrain supplement (number-line/image) chips so
            // they don't expand to full width and squeeze the adjacent text chips into a clipped glyph.
            const isSupplement = !!getSmallSupplement(item.value);
            return (
              <OrderTableCell key={item.id} position={index + 1}>
                <div
                  className={cx(
                    'flex items-stretch',
                    isSupplement ? SMALL_SUPPLEMENT_WRAPPER_CLASS : 'max-w-full min-w-x0'
                  )}
                >
                  <div className={cx('max-w-full min-w-x0', {'flex-1': isSupplement})}>
                    <SupplementChip
                      value={item.value}
                      size='small'
                      className='cursor-default!'
                      nested
                      hasMinTargetWidth
                    />
                  </div>
                </div>
              </OrderTableCell>
            );
          })}
        </OrderTable>
      )}
    </>
  );
};

export default CorrectAnswerSection;
